/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import classNames from 'classnames';
import PropTypes from "prop-types";
import React from 'react';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiIcon } from '../icon';
import { useI18nCompleteStep, useI18nDisabledStep, useI18nErrorsStep, useI18nIncompleteStep, useI18nStep, useI18nWarningStep, useI18nLoadingStep, useI18nCurrentStep } from './step_strings';
import { EuiLoadingSpinner } from '../loading';
import { useEuiTheme } from '../../services';
import { euiStepNumberStyles, euiStepNumberContentStyles } from './step_number.styles';
export var STATUS = ['incomplete', 'disabled', 'loading', 'warning', 'danger', 'complete', 'current'];
export var EuiStepNumber = ({
  className,
  status,
  number,
  titleSize = 's',
  ...rest
}) => {
  const ariaLabelsMap = {
    step: useI18nStep({
      number
    }),
    current: useI18nCurrentStep({
      number
    }),
    incomplete: useI18nIncompleteStep({
      number
    }),
    complete: useI18nCompleteStep({
      number
    }),
    disabled: useI18nDisabledStep({
      number
    }),
    warning: useI18nWarningStep({
      number
    }),
    danger: useI18nErrorsStep({
      number
    }),
    loading: useI18nLoadingStep({
      number
    })
  };
  const classes = classNames('euiStepNumber', className);
  const euiTheme = useEuiTheme();
  const styles = euiStepNumberStyles(euiTheme);
  const cssStyles = [styles.euiStepNumber, styles[titleSize], status && styles[status]];
  const contentStyles = euiStepNumberContentStyles(euiTheme);
  let content;
  let screenReaderText;
  switch (status) {
    // Loading spinner
    case 'loading':
      screenReaderText = ariaLabelsMap.loading;
      content = <EuiLoadingSpinner className="euiStepNumber__loader" size={titleSize === 'xs' ? 'l' : 'xl'} />;
      break;
    // Statuses with icons
    case 'danger':
    case 'warning':
    case 'complete':
      const cssIconStyles = [contentStyles.euiStepNumber__icon, contentStyles[status]];
      const iconTypeMap = {
        danger: 'cross',
        warning: 'warning',
        complete: 'check'
      };
      content = <EuiIcon type={iconTypeMap[status]} aria-label={ariaLabelsMap[status]} size={titleSize === 'xs' ? 's' : 'm'} className="euiStepNumber__icon" css={cssIconStyles} />;
      break;
    // Statuses with numbers
    case 'incomplete':
    case 'current':
    case 'disabled':
    default:
      const cssNumberStyles = [contentStyles.euiStepNumber__number, status && contentStyles[status]];
      screenReaderText = ariaLabelsMap[status || 'step'];
      content = <span aria-hidden="true" className="euiStepNumber__number" css={cssNumberStyles}>
          {number}
        </span>;
      break;
  }
  return <span className={classes} css={cssStyles} {...rest}>
      {screenReaderText && <EuiScreenReaderOnly>
          <span>{screenReaderText}</span>
        </EuiScreenReaderOnly>}
      {content}
    </span>;
};
EuiStepNumber.propTypes = {
  /**
     * May replace the number provided in props.number with alternate styling
     */
  status: PropTypes.any,
  number: PropTypes.number,
  /**
     * Title sizing equivalent to EuiTitle, but only `m`, `s` and `xs`. Defaults to `s`
     */
  titleSize: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiStepNumber.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/steps/step_number.tsx',
    description: '',
    displayName: 'EuiStepNumber',
    methods: [],
    props: {
      status: {
        defaultValue: null,
        description: 'May replace the number provided in props.number with alternate styling',
        name: 'status',
        parent: {
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"warning" | "danger" | "disabled" | "current" | "loading" | "incomplete" | "complete"',
          value: [{
            value: '"warning"'
          }, {
            value: '"danger"'
          }, {
            value: '"disabled"'
          }, {
            value: '"current"'
          }, {
            value: '"loading"'
          }, {
            value: '"incomplete"'
          }, {
            value: '"complete"'
          }]
        }
      },
      number: {
        defaultValue: null,
        description: '',
        name: 'number',
        parent: {
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      titleSize: {
        defaultValue: {
          value: 's'
        },
        description: 'Title sizing equivalent to EuiTitle, but only `m`, `s` and `xs`. Defaults to `s`',
        name: 'titleSize',
        parent: {
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "xs"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['EuiStepNumberProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}